/**
 * @fileoverview Unified Navigation Icon Button
 * Enforces consistent 40x40px touch targets (44px minimum) and hover states
 * across all navigation elements. Airbnb/Apple HIG compliant.
 */

import { forwardRef, createElement, type ReactNode, type ButtonHTMLAttributes, type ElementType } from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { haptics } from "@/lib/haptics";

type HapticType = 'light' | 'medium' | 'selection' | 'none';

export interface NavIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Lucide icon component or custom ReactNode */
  icon: LucideIcon | ReactNode;
  /** Accessible label (always used for aria-label, optionally visible) */
  label?: string;
  /** Show label visibly below icon */
  showLabel?: boolean;
  /** Notification badge count */
  badge?: number;
  /** Active/selected state */
  active?: boolean;
  /** Visual style variant */
  variant?: 'ghost' | 'glass' | 'solid';
  /** Haptic feedback type on click */
  haptic?: HapticType;
  /** Custom class for the icon */
  iconClassName?: string;
}

/**
 * Unified nav button with 40px visual size, 44px touch target, and consistent styling
 */
export const NavIconButton = forwardRef<HTMLButtonElement, NavIconButtonProps>(
  (
    {
      icon: Icon,
      label,
      showLabel = false,
      badge,
      active = false,
      variant = 'ghost',
      haptic = 'selection',
      iconClassName,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (haptic !== 'none') {
        haptics[haptic]();
      }
      onClick?.(e);
    };

    // Detect both function components AND forwardRef/memo components (Lucide icons)
    const isIconComponent = 
      typeof Icon === 'function' ||
      (typeof Icon === 'object' && Icon !== null && '$$typeof' in Icon);

    return (
      <button
        ref={ref}
        onClick={handleClick}
        className={cn(
          // Base: 40px visual with 44px touch target
          "relative inline-flex items-center justify-center",
          "h-10 w-10 min-h-[44px] min-w-[44px]",
          "rounded-full",
          "touch-manipulation",
          "transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          
          // Variant styles
          variant === 'ghost' && [
            "hover:bg-slate-100 dark:hover:bg-white/10",
            "active:bg-slate-200 dark:active:bg-white/15",
          ],
          variant === 'glass' && [
            "bg-white/60 dark:bg-white/10",
            "backdrop-blur-xl",
            "border border-slate-200/60 dark:border-white/10",
            "hover:bg-white/80 dark:hover:bg-white/15",
          ],
          variant === 'solid' && [
            "bg-primary text-primary-foreground",
            "hover:bg-primary/90",
          ],
          
          // Active state
          active && "bg-slate-100 dark:bg-white/15 ring-2 ring-primary/20",
          
          // Tap feedback
          "active:scale-95",
          
          className
        )}
        aria-label={label}
        {...props}
      >
        {/* Icon */}
        {isIconComponent 
          ? createElement(Icon as ElementType, { className: cn("h-5 w-5 text-foreground", iconClassName) })
          : Icon
        }
        
        {/* Badge */}
        {badge !== undefined && badge > 0 && (
          <span 
            className={cn(
              "absolute -top-1 -right-1 h-5 min-w-[20px] px-1",
              "rounded-full bg-destructive text-destructive-foreground",
              "text-[10px] font-bold flex items-center justify-center",
              "pointer-events-none"
            )}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        
        {/* Visible label (optional) */}
        {showLabel && label && (
          <span className="sr-only">{label}</span>
        )}
      </button>
    );
  }
);

NavIconButton.displayName = "NavIconButton";

export default NavIconButton;
